import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateInputPicker } from '@/components/ui/date-input-picker';

interface EditableFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}

export function EditableField({
  label,
  value,
  isEditing,
  onChange,
  placeholder,
  multiline = false,
  rows = 3,
}: EditableFieldProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={!isEditing}
          rows={rows}
          className={!isEditing ? 'resize-none' : ''}
        />
      ) : (
        <Input
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={!isEditing}
        />
      )}
    </div>
  );
}

interface EditableSelectFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function EditableSelectField({
  label,
  value,
  isEditing,
  onChange,
  options,
  placeholder = 'Select...',
}: EditableSelectFieldProps) {
  const selectedOption = options.find(opt => opt.value === value);
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {isEditing ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={selectedOption?.label ?? value}
          readOnly
        />
      )}
    </div>
  );
}

interface EditableDateFieldProps {
  label: string;
  value: string;
  isEditing: boolean;
  onChange: (value: string) => void;
}

export function EditableDateField({
  label,
  value,
  isEditing,
  onChange,
}: EditableDateFieldProps) {
  const handleDateChange = (date: Date | undefined) => {
    onChange(date ? date.toISOString().split('T')[0] : '');
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {isEditing ? (
        <DateInputPicker
          date={value ? new Date(value) : undefined}
          onDateChange={handleDateChange}
        />
      ) : (
        <Input
          value={value ? new Date(value).toLocaleDateString() : ''}
          readOnly
        />
      )}
    </div>
  );
}
